/**
 * Barcode scanning - browser implementation.
 *
 * Desktop USB and Bluetooth barcode scanners are HID keyboards: they "type"
 * the code and then send Enter. No camera and no permissions are involved,
 * which is why this - not the camera - is the primary web scan path:
 *
 *     Scanner -> keydown burst -> barcode value -> product search -> cart
 *
 * Distinguishing a scanner from a cashier typing comes down to speed. A
 * scanner emits its characters in a tight burst (single-digit milliseconds
 * apart); a person cannot. Anything slower than `maxKeyIntervalMs` resets the
 * buffer, so normal typing in a text field is never mistaken for a scan.
 */

export type BarcodeScannerOptions = {
  onScan: (barcode: string) => void;
  minLength?: number;
  maxKeyIntervalMs?: number;
};

export const supportsKeyboardWedge = true;

const DEFAULT_MIN_LENGTH = 3;
const DEFAULT_MAX_KEY_INTERVAL_MS = 60;

/**
 * Typing into a real input is the operator entering data, not a scan - unless
 * the burst is faster than a human can type, which the interval check covers.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!target || !(target as HTMLElement).tagName) {
    return false;
  }
  const element = target as HTMLElement;
  const tag = element.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || element.isContentEditable;
}

export function startKeyboardWedgeScanner(options: BarcodeScannerOptions): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const {
    onScan,
    minLength = DEFAULT_MIN_LENGTH,
    maxKeyIntervalMs = DEFAULT_MAX_KEY_INTERVAL_MS,
  } = options;

  let buffer = '';
  let lastKeyTime = 0;

  const reset = () => {
    buffer = '';
    lastKeyTime = 0;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    /*
     * Not every "keydown" carries a key.
     *
     * Chrome's autofill - and password managers, and anything else that
     * replays input - dispatches synthetic keydown events, and a plain
     * `new Event('keydown')` has no `key` property at all. Reading
     * `event.key.length` on one of those threw
     * "Cannot read properties of undefined (reading 'length')" and took the
     * whole page down the moment the email field was autofilled.
     *
     * A hardware scanner is a HID keyboard and always reports a key, so
     * ignoring an event without one costs nothing and keeps a synthetic
     * burst from being mistaken for a scan.
     */
    if (typeof event.key !== 'string') {
      return;
    }

    // Let the browser handle shortcuts; a scanner never sends modifiers.
    if (event.ctrlKey || event.altKey || event.metaKey) {
      reset();
      return;
    }

    const now = Date.now();
    const gap = lastKeyTime === 0 ? 0 : now - lastKeyTime;

    // Too slow to be a scanner - start over from this keystroke.
    if (gap > maxKeyIntervalMs) {
      buffer = '';
    }
    lastKeyTime = now;

    if (event.key === 'Enter') {
      const scanned = buffer;
      reset();

      if (scanned.length >= minLength) {
        // A completed scan belongs to the POS, not to whatever had focus.
        if (isTextEntryTarget(event.target)) {
          event.preventDefault();
        }
        onScan(scanned);
      }
      return;
    }

    // Printable characters only; ignore Tab/Shift/arrows and friends.
    if (event.key.length === 1) {
      buffer += event.key;
    }
  };

  // Capture phase so the scan is seen even when a modal or input has focus.
  document.addEventListener('keydown', handleKeyDown, true);

  return () => {
    document.removeEventListener('keydown', handleKeyDown, true);
    reset();
  };
}
